import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
// eslint-disable-next-line td-wb-rules/uirouter-import-rule
import { RedirectToResult, Transition } from '@uirouter/core';
import { Account, AccountContextService, AccountHelper, AccountPlatform, AccountsContainer, AccountType, ConfigurationService, UserPreferenceStrategyType } from '@tdwb/core';

@Injectable({ providedIn: 'root' })
export class StockLendingService {
  private readonly accountContextService = inject(AccountContextService);
  private readonly configurationService = inject(ConfigurationService);

  readonly refreshStockLendingTab: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  resolveRedirectionRoute(transition: Transition): Promise<string | RedirectToResult> {
    const accessToStockLendingPromise = transition.injector().getAsync('doesUserHaveAccessToStockLending');
    const referrerPromise = transition.injector().getAsync('referrer');
    const accountsContainerPromise = transition.injector().getAsync('accountsContainer');
    return Promise.all([accessToStockLendingPromise, referrerPromise, accountsContainerPromise])
      .then(([accessToStockLending, referrer, accountsContainer]: [boolean, string, AccountsContainer]) => {
        if (accessToStockLending === false) {
          return 'page.account.holdings';
        } else if (referrer === 'wb-direct-investing') {
          return this.determineRedirectionRoute(accountsContainer);
        }
        return '';
      });
  }

  private determineRedirectionRoute(accountsContainer: AccountsContainer): string | RedirectToResult {
    const accounts = accountsContainer?.getAccounts() || [];
    const selectedAccountId = this.accountContextService.getSelectedAccountId();
    const selectedAccount = accounts.find((account: Account): boolean => account.id === selectedAccountId);
    const isDICashAccount = AccountHelper.isDICashAccount(selectedAccount);
    const preferredLinkId = this.configurationService.getProperty<string>('userPreferences.WBT_START_PAGE');
    const accountNavigationStrategy: UserPreferenceStrategyType = this.configurationService.getProperty<UserPreferenceStrategyType>('userPreferences.ACCOUNT_NAVIGATION_STRATEGY');
    if (!isDICashAccount) {
      const diAccounts: Array<Account> = accounts.filter((account: Account): boolean => account.accountPlatform === AccountPlatform.DIRECT_INVESTMENT);
      if (diAccounts.length > 0) {
        const diCashAccount = diAccounts.find((account: Account): boolean => account.type.acctType === AccountType.CASH);
        const targetAccount = diCashAccount ? diCashAccount : !AccountHelper.isDIAccount(selectedAccount) ? diAccounts[0] : undefined;
        if (targetAccount) {
          this.accountContextService.setSelectedAccountId(targetAccount.id);
          this.accountContextService.setSelectedAccountGroupId(targetAccount.groupId);
          // If user preferred landing page is holdings page or account navigation strategy is default, refresh the account selection with the target account
          if (preferredLinkId === 'link_holdings' || accountNavigationStrategy === UserPreferenceStrategyType.DEFAULT_ACCOUNT) {
            return {
              state: '',
              params: {
                accountId:  targetAccount.id
              }
            };
          }
        }
      }
    }
    return '';
  }
}
